import { useQuery } from '@tanstack/react-query'
import { useOrganization } from '@/contexts/OrganizationContext'
import { getSupabaseClient } from '@/lib/supabase'
import { cashPositionPeriods, type CashPositionPeriod } from '@/lib/date-utils'
import {
  CASH_POSITION_SUMMARIES_KEY,
  emptyTotal,
  mapRow,
  type CashPositionRpcRow,
  type CashPositionSummary,
} from './cashPositionSummaries'
import type { CashPositionFilterState } from '@/components/banking/cashPositionColumns'

/**
 * One row from `get_cash_position_summary_multi_period` — the grouped per-facility
 * shape (facility / pack / total) plus a `period_index` discriminating which of
 * the five forecast windows the row belongs to (DCP-144). Numerics arrive as
 * PostgREST strings and are coerced by {@link mapRow}.
 */
interface MultiPeriodRpcRow extends CashPositionRpcRow {
  row_type: 'facility' | 'pack' | 'total'
  pack_id: string | null
  pack_name: string | null
  /** 0 = remainder of current week, 1..4 = weeks 1..4 out. */
  period_index: number
  // NB: the RPC returns rows already ordered by period_index then the DCP-145
  // bucket order (total → packs expanded → standalone), so the hook builds its
  // column order purely from first appearance and doesn't read any sort column.
}

/**
 * One rendered data column (a facility or a pack subtotal) carrying its summary
 * for EACH of the five periods, indexed 0..4 by `period_index`.
 */
export interface CashPositionMultiPeriodColumn {
  /** Stable React/`colgroup` key: facility id, or `pack:<id>` for a subtotal. */
  key: string
  /** Column kind — a facility's own column or a pack SUBTOTAL column. */
  kind: 'facility' | 'pack'
  /**
   * Pack membership exactly as the RPC stamped it on the row: the pack's own id
   * on a subtotal column, the owning pack's id on an in-pack facility column,
   * and null on a standalone facility.
   *
   * Carried on the column itself, rather than looked up per-facility from a
   * second source (e.g. the `useFacilities` catalog), because the SUBTOTAL
   * FIGURE is a `SUM` over the pack's visible members computed inside this
   * same RPC (DCP-161). If the warning aggregator grouped members by any other
   * source, the two would only agree by coincidence — a stale or still-loading
   * catalog could silently under-count the subtotal's warning while the
   * facility columns underneath (and the Combined Total) still warn, breaking
   * the documented invariant that an amber subtotal implies an amber member.
   */
  packId: string | null
  /** Display name (facility name or pack name). */
  name: string
  /** Summaries by period index (always length 5; missing slots are all-zero). */
  periods: CashPositionSummary[]
}

/** Result of {@link useCashPositionSummaryMultiPeriod} — the grid's render model. */
export interface CashPositionMultiPeriod {
  /** Ordered columns (facilities + pack subtotals) as the RPC returned them. */
  columns: CashPositionMultiPeriodColumn[]
  /** Field-wise Combined Total by period index (always length 5). */
  combinedTotal: { periods: CashPositionSummary[] }
  /** The five period descriptors, in display order — drives block headings. */
  periodMeta: CashPositionPeriod[]
  // NB: no as-of date here. The header used to show the most-recent bank-balance
  // date across the visible scope; since DCP-293 it always shows the current date,
  // computed in the page, so the RPC's `as_of_date` is no longer surfaced.
}

/**
 * Multi-period Cash Position Summary (DCP-144) — extends the current-week grouped
 * view (DCP-145) across five forecast windows: the remainder of the current week
 * plus the four following full calendar weeks. ALL computation — per-facility
 * math, pack subtotals, the filter-aware Combined Total, period chaining of the
 * ending Book Balance, filtering and ordering — happens server-side in
 * `get_cash_position_summary_multi_period`. This hook is a thin mapper: it sends
 * the period ranges + filter selection and pivots the returned, already-ordered
 * rows into a per-column, per-period render model.
 *
 * The query key is prefixed with {@link CASH_POSITION_SUMMARIES_KEY} so the
 * existing invalidations on projections / balances / bank accounts / facilities /
 * sage checks continue to refresh this view unchanged.
 *
 * Empty `packIds`/`facilityIds` are sent as `undefined` so the RPC's DEFAULT NULL
 * applies ("All" mode), matching the prior single-week hook's convention.
 *
 * @param filters - the current pack/facility selection
 */
export function useCashPositionSummaryMultiPeriod(filters: CashPositionFilterState) {
  const { currentOrganization } = useOrganization()
  const orgId = currentOrganization?.id

  // The ordered 5-period ladder (remainder + 4 full weeks). Computed once per
  // render; its start/end arrays drive both the RPC params and the query key.
  const periods = cashPositionPeriods()
  const periodStarts = periods.map((p) => p.start)
  const periodEnds = periods.map((p) => p.end)

  const { packIds, facilityIds } = filters

  return useQuery<CashPositionMultiPeriod>({
    queryKey: [
      ...CASH_POSITION_SUMMARIES_KEY,
      'multi-period',
      orgId,
      periodStarts,
      periodEnds,
      packIds,
      facilityIds,
    ],
    queryFn: async () => {
      if (!orgId) {
        throw new Error('No organization selected')
      }

      const supabase = getSupabaseClient()
      const { data, error } = await supabase.rpc('get_cash_position_summary_multi_period', {
        p_org_id: orgId,
        p_period_starts: periodStarts,
        p_period_ends: periodEnds,
        // Empty selection → omit (undefined) so the RPC's DEFAULT NULL applies,
        // i.e. "All" mode.
        p_pack_ids: packIds.length ? packIds : undefined,
        p_facility_ids: facilityIds.length ? facilityIds : undefined,
      })
      if (error) throw new Error(error.message)

      const rows = (data ?? []) as MultiPeriodRpcRow[]
      const periodCount = periods.length

      // Fresh all-zero period array so every column/total is defensively length 5
      // even if the RPC omits a period slot for some column.
      const emptyPeriods = () => Array.from({ length: periodCount }, () => emptyTotal())

      // Columns keyed by identity; order preserved by first appearance (period 0
      // rows come first and already carry the DCP-145 bucket order).
      const columnMap = new Map<string, CashPositionMultiPeriodColumn>()
      const columnOrder: string[] = []
      const combinedTotalPeriods = emptyPeriods()

      for (const row of rows) {
        const idx = row.period_index
        // Guard against an out-of-range index so a bad row can't write past the
        // fixed-length period arrays.
        if (idx < 0 || idx >= periodCount) continue

        const summary = mapRow(row)

        if (row.row_type === 'total') {
          combinedTotalPeriods[idx] = summary
          continue
        }

        // Facility columns key on facility id; pack subtotals on `pack:<id>`.
        const key = row.row_type === 'pack' ? `pack:${row.pack_id}` : (row.facility_id as string)
        let column = columnMap.get(key)
        if (!column) {
          column = {
            key,
            kind: row.row_type === 'pack' ? 'pack' : 'facility',
            // The RPC sets `pack_id` on BOTH row types: its own id on a pack
            // subtotal row, and the owning pack's id on a facility row that is
            // in a visible pack (`CASE WHEN sh.in_pack THEN sh.pack_id END`),
            // hence null for a standalone facility. Passed through unchanged —
            // see the doc comment on `packId` for why this must stay the ONLY
            // source the warning aggregator consults.
            packId: row.pack_id,
            name: row.row_type === 'pack' ? (row.pack_name ?? 'Pack') : row.facility_name,
            periods: emptyPeriods(),
          }
          columnMap.set(key, column)
          columnOrder.push(key)
        }
        column.periods[idx] = summary
      }

      return {
        columns: columnOrder.map((key) => columnMap.get(key) as CashPositionMultiPeriodColumn),
        combinedTotal: { periods: combinedTotalPeriods },
        periodMeta: periods,
      }
    },
    enabled: !!orgId,
    // Keep the previous result visible while a FILTER/PERIOD change refetches —
    // the pack/facility selection is part of the queryKey, so without this `data`
    // resets to undefined on every change and `isLoading` flips back to true.
    // That would not only blank the grid to a skeleton on each filter tweak but
    // (since the filter controls now live in the persistent page header, gated
    // on the same loading flag) make the very control being used disappear
    // mid-interaction.
    //
    // BUT scope the carry-over to the SAME org: `orgId` is also part of the
    // queryKey (index 2, after CASH_POSITION_SUMMARIES_KEY + 'multi-period'), and
    // platform staff can switch org WITHOUT remounting this page. Carrying the
    // previous org's cash figures over as if current — with isLoading=false —
    // would be a serious financial-data leak, and this key isn't in the org-
    // switch invalidation set. So only reuse the placeholder when the previous
    // query's org matches; otherwise fall back to a fresh load (DCP-306 review #4).
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[2] === orgId ? previousData : undefined,
  })
}
